import React, { useRef, useEffect, useState } from 'react' // Add useEffect and useState
import { useLocation, useNavigate } from 'react-router-dom'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import RadarChartComponent from './RadarChartComponent'
import axios from 'axios'

import {
  BrainCircuit,
  Users,
  RefreshCcw,
  Layers,
  HelpCircle,
  Locate,
  FileCheck,
  LifeBuoy,
  BookMarked,
  ShieldAlert,
  LogOut,
  Wallet,
  Users2,
  Clock,
  MessageCircle,
  SwitchCamera,
  BadgeCheck,
  FileText,
  Repeat,
  BarChart,
  TrendingUp, 
  AlertTriangle,
  ListChecks,
  UserCheck,
  ShieldCheck,
} from 'lucide-react'

export const SUB_QUESTION_ICONS = {
  internal_skills: BrainCircuit,            // Technical capability
  external_partners: Users,                 // External partners
  readiness: RefreshCcw,                    // Cultural readiness
  alignment: Layers,                        // Strategic alignment
  problem_validity: HelpCircle,             // Problem clarity
  user_needs: Locate,                       // User needs understanding
  solution_rationale: FileCheck,            // Solution rationale
  future_support: LifeBuoy,                 // Future support
  knowledge_retention: BookMarked,          // Knowledge retention
  risk_awareness: ShieldAlert,              // Risk awareness
  exit_strategy: LogOut,                    // Exit strategy
  budget_fit: Wallet,                       // Budget fit
  staff_capacity: Users2,                   // Staff capacity
  timeline_realism: Clock,                  // Timeline realism
  inclusive_process: MessageCircle,         // Inclusive process
  alternatives_considered: SwitchCamera,    // Alternatives considered
  past_experience: BadgeCheck,              // Past experience
  reflection_routines: FileText,            // Reflection routines
  adaptability: Repeat,                     // Adaptability
  success_definition: BarChart,             // Success definition
}


const DIMENSION_ADVICE = {
  capability: {
    title: 'Capability fit',
    strength: "You’re in a good position to consider more complex or customised solutions, including building or significantly adapting tools, because you have the internal capability to support, test, and evolve them. Lean into your team’s skills and consider co-creating solutions with trusted suppliers or peers.",
    weakness: "Avoid bespoke builds or complex integrations unless you're paired with a strong external partner and long-term support plan. Focus on well-supported, off-the-shelf tools with strong documentation and user communities. Invest in upskilling and/or trusted external advice.",
    neutral: "You have enough internal understanding to manage simple digital projects with some external input. Focus on building confidence by choosing tools with good support and documentation. Consider pairing with a knowledgeable partner or advisor to de-risk next steps.",
    summary_strength: "You have the in-house skills to handle complex digital work with confidence.",
    summary_weakness: "You’ll need external help or simpler tools to avoid overreaching your team’s capability."
  },
  culture: {
    title: 'Cultural readiness',
    strength: "Your people are open to change, that gives you room to experiment, prototype, and iterate. Involve cross-functional teams early, and consider collaborative procurement or design processes. Culture is your advantage.",
    weakness: "Focus on small, visible wins. Prioritise tools that minimise disruption and come with good onboarding and support. Avoid making decisions in isolation. Involve frontline teams early and build internal trust before launching major changes.",
    neutral: "There’s some openness to change, but enthusiasm varies. Prioritise clear internal communication and early involvement of key teams. Consider running short pilots to build confidence and momentum.",
    summary_strength: "You’ve got a culture that supports change and digital experimentation.",
    summary_weakness: "Change may be resisted — start small and build trust before moving fast."
  },
  strategy: {
    title: 'Strategic fit',
    strength: "Your digital decision clearly supports your broader organisational goals. That gives you the credibility and focus to align teams, secure investment, and plan for long-term success. Use this clarity to define clear metrics for success and evaluation.",
    weakness: "Pause and revisit the “why” behind the decision. Is this solving a real, clearly articulated need or just a perceived one? Map the decision to your mission and strategic goals before proceeding. If the connection isn't clear, you may be wasting effort.",
    neutral: "The strategic connection is emerging but not fully defined. Clarify how this decision supports your organisational goals and communicate this internally to build shared understanding.",
    summary_strength: "This decision is clearly aligned to your wider strategic goals.",
    summary_weakness: "You’re at risk of misalignment — revisit how this supports your mission."
  },
  ownership: {
    title: 'Ownership & longevity',
    strength: "You’re well-positioned to invest in platforms or builds that evolve over time. Choose tools that give you control and flexibility. Ensure documentation and knowledge-sharing are embedded so others can maintain and adapt the system in future.",
    weakness: "Avoid over-committing to long-term tech without clear plans for support, updates, or succession. Choose options with external support packages, auto-updates, or low-maintenance needs. Document decisions now to avoid future handover chaos.",
    neutral: "Some ownership practices are in place, but gaps remain. Strengthen your ability to maintain and evolve the solution by improving documentation and support planning.",
    summary_strength: "You’re well set up to own and sustain this decision over time.",
    summary_weakness: "You lack support structures to ensure this decision lasts — proceed with caution."
  },
  risk: {
    title: 'Risk & dependency',
    strength: "You’ve likely already considered risk exposure and have contingency plans. Use this strength to push for transparent contracts, open standards, and ethical supplier relationships. You can afford to be strategic and assertive in procurement.",
    weakness: "Before moving forward, identify your blind spots. What happens if your supplier disappears? Who owns your data? Choose tools with clear exit strategies and minimal lock-in. Ask harder questions even if they’re uncomfortable.",
    neutral: "You’re aware of some risks but haven’t fully planned for them. Take time to identify key dependencies and think through exit strategies. Use a risk checklist before finalising your decision.",
    summary_strength: "You’ve already considered risks and planned how to manage them.",
    summary_weakness: "You may be exposed to risk or supplier dependency — address this first."
  },
  resourcing: {
    title: 'Resourcing & time',
    strength: "You’ve got the capacity to do this properly. Make sure you’re using that time not just to launch but to onboard, adapt, and iterate. Allocate time for training, feedback, and slow culture change. Plan beyond the “go live” moment.",
    weakness: "Don’t start big. Scope tightly. Use tools that allow for phased delivery, starter versions, or lightweight pilots. Communicate clearly with leadership and funders about what’s realistic and what’s at risk if it’s rushed or under-resourced.",
    neutral: "You’ve got a reasonable starting point, but delivery may be tight. Focus on keeping scope manageable and building in buffer for iteration and support.",
    summary_strength: "You have the people and time to support this work properly.",
    summary_weakness: "Delivery may be rushed or under-resourced — reduce scope or find support."
  },
  decision: {
    title: 'Decision-making quality',
    strength: "You’ve created the conditions for thoughtful, informed, and transparent digital decision-making — a rare and valuable strength. Leverage it by documenting the process, sharing your rationale, and using it as a model for future work. Bring that clarity into procurement, partnerships, and board-level conversations.",
    weakness: "The process may have been too narrow or rushed. Revisit your assumptions, involve affected voices, and prioritise clarity over speed. Poor decision-making is a frequent root cause of digital failure. A poor or narrow decision-making process can undo even well-scoped projects.",
    neutral: "The decision-making process is broadly sound but could be strengthened. Take time to invite challenge, revisit alternatives, and document your rationale before moving forward.",
    summary_strength: "You’ve made thoughtful, transparent digital decisions.",
    summary_weakness: "Your decision-making process may have been rushed or too narrow."
  },
  experience: {
    title: 'Experience fit',
    strength: "Your previous experience gives you an edge. Use it to anticipate challenges, build on past success, and mentor others. Document your decision process and actively reference what worked (and didn’t) last time.",
    weakness: "Tread carefully. If this is unfamiliar territory, start with pilots, peer advice, and scenario planning. Learn from others — and create time to reflect as you go. Prioritise documentation and knowledge-sharing to avoid repeating mistakes.",
    neutral: "You’ve got some relevant experience to build on. Refer to what you’ve done before, and plan space to reflect, adapt, and learn as you go.",
    summary_strength: "You can draw on past experience to shape and deliver this project.",
    summary_weakness: "You’re in unfamiliar territory — start small and learn as you go."
  },
  purpose: {
    title: 'User insight & rationale',
    strength: "You’re clear on the problem, the need, and the why. This clarity gives you a strong foundation for good decisions. Use it to set shared success metrics, secure buy-in, and avoid chasing shiny but misaligned solutions.",
    weakness: "Step back and clarify the real problem you’re solving. Don’t pick a tool until there’s agreement on goals, needs, and measures of success. Explore user needs and organisational priorities before committing.",
    neutral: "You’ve got a reasonable idea of purpose, but may still be operating on assumptions. Validate the need and clarify your definition of success to sharpen decision-making.",
    summary_strength: "You have a clear rationale and understanding of what you’re trying to achieve.",
    summary_weakness: "You may be solving the wrong problem — clarify goals and user needs first."
  },
  learning: {
    title: 'Learning & adaptability',
    strength: "Learning is embedded in your organisation’s culture. That sets you up well to reflect, adapt, and continuously improve. Use this to test iteratively, adjust based on feedback, and share insights with the wider sector.",
    weakness: "Without reflection and learning, mistakes will repeat and progress will stall. Create space and permission to reflect. Run post-mortems or learning reviews — and act on what you find.",
    neutral: "There’s some informal learning, but it’s not systematic. Strengthen habits of reflection and share insights internally. Even simple documentation or team check-ins can unlock future improvement.",
    summary_strength: "You’re well equipped to reflect, adapt, and improve over time.",
    summary_weakness: "You risk repeating mistakes — prioritise time and permission to learn."
  },
  internal_skills: {
  strength: "You have solid internal digital skills. This gives you flexibility and confidence to manage, adapt, or even co-create digital solutions. Prioritise tools that give your team room to experiment and evolve.",
  neutral: "You have some internal skills but may need occasional support. Look for well-documented, low-maintenance tools. Consider combining internal delivery with structured external input to avoid skill gaps becoming bottlenecks.",
  weakness: "Your team may lack confidence or capacity with digital tools. Prioritise simple, intuitive solutions and budget for training, onboarding, or external help to avoid future reliance or frustration.",
  summary_strength: "You have strong internal digital skills to support this work.",
  summary_weakness: "Digital delivery may be limited by internal skills or confidence gaps."
},
external_partners: {
  strength: "You have access to trusted external digital partners. Use their expertise to stretch your thinking, support delivery, and bring specialist knowledge into your organisation.",
  neutral: "You have some access to external support, but it may not be consistent or strategic. Clarify your needs and build longer-term relationships with suppliers or advisors you trust.",
  weakness: "You may lack reliable external partners for digital work. Be cautious with large or complex projects. Focus on simple solutions, and invest time in identifying the right kind of external support.",
  summary_strength: "You’re well supported by reliable external digital partners.",
  summary_weakness: "You lack trusted partners — seek support before moving forward."
},
readiness: {
  strength: "There’s strong internal openness to change. This will help you experiment and iterate with confidence. Involve your teams early and treat digital work as a shared journey.",
  neutral: "Your organisation is somewhat open to change, but energy varies. Focus on communication, transparency, and showing small wins to build momentum and trust.",
  weakness: "There may be hesitancy or change fatigue in the organisation. Start small, choose user-friendly tools, and involve staff early to reduce anxiety. Change management matters as much as technology here.",
  summary_strength: "Your teams are open to change and ready to engage with digital work.",
  summary_weakness: "Change fatigue or hesitancy may undermine digital delivery."
},
alignment: {
  strength: "Your digital decision is clearly aligned with your organisation’s strategy. That gives you a strong case for buy-in, investment, and long-term success.",
  neutral: "There’s a general connection to your strategy, but it could be clearer. Sharpen your narrative and success metrics to secure confidence and support.",
  weakness: "The strategic connection is weak or unclear. Pause to revisit how this decision supports your organisation’s mission and priorities — or risk misalignment and friction.",
  summary_strength: "Your decision is clearly aligned with strategic goals.",
  summary_weakness: "Your decision may lack clear strategic justification."
},
problem_validity: {
  strength: "You’ve clearly defined the real-world need behind this decision, a key ingredient for success. This clarity will help keep your focus, secure buy-in, and resist tech-for-tech’s-sake distractions.",
  neutral: "You have a general sense of the problem, but it may need refining. Dig deeper before jumping to solutions; talk to users, review data, or map pain points.",
  weakness: "You may be chasing a perceived problem that isn’t well understood. Take time to validate the need before committing to tools or tech. Otherwise, you risk solving the wrong thing.",
  summary_strength: "You’ve clearly defined a real and relevant digital need.",
  summary_weakness: "You risk solving the wrong problem — revisit the underlying need."
},
user_needs: {
  strength: "You understand your users well, this insight will help you choose or shape a solution that truly meets their needs. Make sure you are regularly refreshing this understanding with additional research.",
  neutral: "You’ve considered user needs but may still be relying on assumptions. Validate them with quick research, surveys, or conversations.",
  weakness: "You don’t yet have a strong understanding of user needs. Avoid committing to tech decisions until you’ve clarified who it’s for and what they need.",
  summary_strength: "You have strong insight into user needs.",
  summary_weakness: "Your understanding of user needs is weak or unclear."
},
solution_rationale: {
  strength: "You’ve clearly thought through why this solution makes sense. That rationale will help secure support and focus your implementation.",
  neutral: "You’ve got a general sense of why this solution fits, but it could be sharper. Revisit the options you considered — can you explain why this one is best?",
  weakness: "Your rationale for this solution is unclear or weak. Step back and clarify the logic — why this solution, why now, and what alternatives were considered?",
  summary_strength: "Your solution rationale is clear and well considered.",
  summary_weakness: "You may not have a clear rationale for this solution."
},
future_support: {
  strength: "You’ve planned for long-term support. This sets you up for sustainable delivery and reduces risk of disruption or abandonment.",
  neutral: "You’ve thought a bit about long-term support, but there may be gaps. Clarify ownership, resourcing, and contingency plans.",
  weakness: "There’s little clarity on how this will be supported in future. Prioritise solutions with clear maintenance models and plan for continuity now — not later.",
  summary_strength: "You’ve planned for the long-term support of this work.",
  summary_weakness: "You risk future instability without a support plan."
},
knowledge_retention: {
  strength: "You’ve embedded knowledge-sharing practices, which will help others maintain and adapt your work in future.",
  neutral: "You’re partially covered, but key knowledge may still sit with a few individuals. Strengthen documentation and cross-team understanding.",
  weakness: "Your knowledge is likely fragile or siloed. Prioritise documenting decisions and processes — especially before anyone leaves or moves on.",
  summary_strength: "Your knowledge is well documented and shareable.",
  summary_weakness: "Key knowledge may be fragile or undocumented."
},
risk_awareness: {
  strength: "You’ve identified key risks and factored them into your planning. That gives you resilience and flexibility when things don’t go to plan.",
  neutral: "You’ve thought about some risks but may have blind spots. Use a checklist or risk mapping exercise to build confidence.",
  weakness: "Risk awareness is low. Before proceeding, identify critical dependencies and failure points — and plan around them.",
  summary_strength: "You’ve identified and planned for key risks.",
  summary_weakness: "You may be exposed to avoidable risk — act now to reduce it."
},
exit_strategy: {
  strength: "You’ve already considered how you’ll exit or change direction if needed. That future thinking will help you avoid lock-in or regret.",
  neutral: "You’ve thought a bit about your exit strategy, but it may not be robust. Clarify what happens if the tool or supplier fails.",
  weakness: "There’s no clear exit plan. That risks supplier lock-in or dependency on fragile tools. Before proceeding, define what flexibility or ability to exit you’d need to stay in control. Don’t proceed without a plan B.",
  summary_strength: "You’ve already planned how to exit or pivot if needed.",
  summary_weakness: "You risk lock-in or fragility without a clear exit plan."
},
budget_fit: {
  strength: "Your budget is well-matched to your ambition. That gives you breathing room to build, adapt, and support the work over time.",
  neutral: "You can probably make it work but you’ll need to stay lean and realistic. Prioritise essentials and avoid over-scoping.",
  weakness: "Your budget is likely too tight for what you hope to achieve. Rescope, phase delivery, or seek additional funding before proceeding.",
  summary_strength: "Your budget matches the scope of your ambition.",
  summary_weakness: "You may not have the budget to deliver this well."
},
staff_capacity: {
  strength: "You’ve got the people in place to do the work well. Use that capacity wisely — and protect time for iteration, not just delivery.",
  neutral: "You have some capacity, but it may be stretched. Be honest about workloads and avoid piling too much on too few people.",
  weakness: "Your team may not have the capacity to deliver this successfully. Don’t proceed without either reducing scope or bringing in support.",
  summary_strength: "Your team has the time and capacity to deliver this well.",
  summary_weakness: "Your team may be overstretched or under-resourced."
},
timeline_realism: {
  strength: "Your timeline is realistic, which reduces stress and improves quality. Use the time to test, onboard, and learn along the way.",
  neutral: "Your timeline might work, but it’s tight. Build in contingency and avoid over-promising.",
  weakness: "The timeline looks unrealistic. Reassess what’s achievable, communicate trade-offs, and avoid rushing into failure.",
  summary_strength: "You’ve allowed enough time to do this properly.",
  summary_weakness: "Your timeline may be unrealistic or overly optimistic."
},
inclusive_process: {
  strength: "You’ve involved a diverse and relevant mix of voices in shaping this decision. That leads to better outcomes and stronger ownership.",
  neutral: "You’ve consulted a few people, but it may not be broad or deep enough. Consider who else should be involved — especially those impacted.",
  weakness: "Your decision-making has been narrow. Involve people early — especially users, delivery teams, and those who’ll maintain or live with the outcome.",
  summary_strength: "Your process has involved relevant voices and perspectives.",
  summary_weakness: "Important voices may have been excluded from the process."
},
alternatives_considered: {
  strength: "You’ve clearly considered multiple options before choosing this path. That clarity and confidence will help you justify and stick to your direction.",
  neutral: "You’ve looked at some options, but the rationale could be stronger. Revisit what else was on the table and why this was the right call.",
  weakness: "You may have defaulted to a solution without exploring viable alternatives, possibly shaped by supplier influence. Take time to step back and consider whether this is truly the best fit.",
  summary_strength: "You’ve considered and ruled out alternatives with confidence.",
  summary_weakness: "You may not have explored or documented viable alternatives."
},
past_experience: {
  strength: "You’ve done similar work before. Build on those lessons and avoid previous pitfalls. Share what you know with your team and partners.",
  neutral: "You’ve got some relevant experience. Use it as a reference point but remain open to learning and adapting.",
  weakness: "This is new territory. Proceed carefully, seek advice, and make time to learn as you go. Start small and stay reflective.",
  summary_strength: "You can draw on previous relevant experience.",
  summary_weakness: "This is unfamiliar ground — move slowly and seek input."
},
reflection_routines: {
  strength: "You have strong habits of reflection and learning. Use these to evaluate, adapt, and improve as the work unfolds.",
  neutral: "You reflect occasionally, but it’s not systematic. Consider lightweight review rituals to strengthen learning culture.",
  weakness: "Reflection is missing or informal. Introduce simple check-ins, post-mortems, or feedback loops to improve over time.",
  summary_strength: "You’ve built strong habits of reflection and learning.",
  summary_weakness: "You may not be learning from previous projects or choices."
},
adaptability: {
  strength: "Your organisation adapts well to change. That flexibility will help you iterate and respond to unexpected challenges.",
  neutral: "You can adapt if needed but it may take effort. Build shared understanding and align processes to improve responsiveness.",
  weakness: "You struggle to adapt quickly. Be cautious with dynamic or complex projects. Start with small, structured change and grow confidence gradually.",
  summary_strength: "You’re flexible and can adapt as circumstances change.",
  summary_weakness: "You may find it hard to pivot or adjust once things are underway."  
}
,
success_definition: {
  strength: "You have a shared, well-articulated definition of success and you’re set up to measure it. This clarity will help you stay focused, communicate progress, and make smart choices when priorities shift. Use this foundation to shape KPIs, team goals, and evaluation plans.",
  neutral: "You’ve talked about success, but it may not be fully defined or consistently understood. Take time to align your team around what success looks like and how it will be measured. A shared understanding now can prevent confusion and drift later.",
  weakness: "You’re at risk of moving forward without knowing what success looks like. That makes it hard to prioritise, stay on track, or evaluate the outcome. Before committing further, agree on your goals, success measures, and how you’ll track them.",
  summary_strength: "You’ve clearly defined success and how you’ll measure it.",
  summary_weakness: "Success is not well defined — clarify what good looks like."
}
}

const getRAGStatus = (score) => {
  if (score <= 2) return 'red'
  if (score === 3) return 'amber'
  if (score >= 4) return 'green'
}

const DIMENSION_IDS = {
  capability: ['internal_skills', 'external_partners'],
  culture: ['readiness'],
  strategy: ['alignment', 'problem_validity', 'success_definition'],
  purpose: ['user_needs', 'solution_rationale'],
  ownership: ['future_support', 'knowledge_retention'],
  risk: ['risk_awareness', 'exit_strategy'],
  resourcing: ['budget_fit', 'staff_capacity', 'timeline_realism'],
  decision: ['inclusive_process', 'alternatives_considered'],
  experience: ['past_experience'],
  learning: ['reflection_routines', 'adaptability'],
}

const SUB_QUESTION_LABELS = {
  internal_skills: 'Internal digital skills',
  external_partners: 'Trusted external partners',
  readiness: 'Cultural readiness',
  alignment: 'Strategic alignment',
  problem_validity: 'Problem clarity',
  user_needs: 'User needs understanding',
  solution_rationale: 'Solution rationale',
  future_support: 'Future support planning',
  knowledge_retention: 'Knowledge retention',
  risk_awareness: 'Risk awareness',
  exit_strategy: 'Exit strategy',
  budget_fit: 'Budget fit',
  staff_capacity: 'Staff capacity',
  timeline_realism: 'Timeline realism',
  inclusive_process: 'Inclusive process',
  alternatives_considered: 'Alternatives considered',
  past_experience: 'Relevant past experience',
  reflection_routines: 'Reflection routines',
  adaptability: 'Adaptability',
  success_definition: 'Definition of success',
}


const hasMixedSignals = (subScores, subQuestionIds) => {
  let hasLow = false
  let hasHigh = false

  subQuestionIds.forEach((id) => {
    const score = subScores[id]
    if (score <= 2) hasLow = true
    if (score >= 4) hasHigh = true
  })

  return hasLow && hasHigh
}

function ResultsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { scores = {}, subScores = {} } = location.state || {}

  const [sectorAverages, setSectorAverages] = useState(null)
  const [showSectorAverage, setShowSectorAverage] = useState(false)
  const [sectorReadinessAverage, setSectorReadinessAverage] = useState(null)

  // --- Email gate state (unlock full results) ---
  const [isUnlocked, setIsUnlocked] = useState(true)
  const [gateEmail, setGateEmail] = useState('')
  const [gateError, setGateError] = useState('')
  const [gateLoading, setGateLoading] = useState(false)

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  const handleUnlock = async () => {
    setGateError('')
    if (!isValidEmail(gateEmail)) {
      setGateError('Please enter a valid email address')
      return
    }
    try {
      setGateLoading(true)
      // POST to your Netlify function that adds the member to Ghost
      await axios.post('/.netlify/functions/subscribeToGhost', {
        email: gateEmail,
        scores,      // optional: include data for your own analytics
        subScores,   // optional
        source: 'decision-fit-results'
      })
      localStorage.setItem('resultsEmail', gateEmail)
      setIsUnlocked(true)
    } catch (err) {
      console.error('Ghost subscription failed:', err)
      setGateError('Something went wrong subscribing. Please try again.')
    } finally {
      setGateLoading(false)
    }
  }



  const dimensionKeysWithMixedSignals = Object.entries(DIMENSION_IDS)
  .filter(([key, subQuestionIds]) => {
    const subScoresForDimension = subQuestionIds
      .map((id) => subScores[id])
      .filter((s) => typeof s === 'number')
    return subScoresForDimension.length >= 2 && hasMixedSignals(subScores, subQuestionIds)
  })
  .map(([key]) => key)

  const hasAnyMixedSignals = dimensionKeysWithMixedSignals.length > 0


  const resultsRef = useRef()

  const handleGoBack = () => navigate('/')

  const handleDownloadPDF = async () => {
  const input = resultsRef.current

  // Capture with lower scale for smaller image
  const canvas = await html2canvas(input, { scale: 2 })

  // Convert canvas to JPEG with compression (quality 0.7)
  const imgData = canvas.toDataURL('image/jpeg', 0.7)

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'px',
    format: [canvas.width, canvas.height + 60],
  })

  pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height)

  pdf.setFontSize(10)
  pdf.setTextColor(100)
  pdf.text(
    'These results were generated from the Decision Fit Canvas, a free tool developed by Ash Mann. Ash is a consultant that helps cultural organisations with their digital thinking and delivery. There are more free tools at ashmann.co/tools',
    40,
    canvas.height + 40,
    { maxWidth: canvas.width - 80 }
  )

  pdf.save('DecisionFitResults.pdf')
}


useEffect(() => {
  const fetchAverages = async () => {
    try {
      const response = await axios.get('/.netlify/functions/fetch-averages')
      const { sectorAverages, overallReadinessAverage } = response.data
      setSectorAverages(sectorAverages)
      setSectorReadinessAverage(overallReadinessAverage)
    } catch (error) {
      console.error('Failed to fetch sector averages:', error)
    }
  }

  fetchAverages()
}, [])

  // Calculate overall score from 20 sub-questions (each out of 5)
  const subScoreValues = Object.values(subScores).filter((s) => typeof s === 'number')
  const overallScoreOutOf100 = subScoreValues.length > 0
  ? Math.round((subScoreValues.reduce((a, b) => a + b, 0) / 10) * 10)
  : null


  const strengths = []
  const weaknesses = []
  const neutrals = []

  Object.entries(scores).forEach(([key, val]) => {
    if (val >= 4) strengths.push(key)
    if (val == 3) neutrals.push(key)
    if (val <= 2) weaknesses.push(key)
  })

  return (
    <div ref={resultsRef} className="page-container">
      <div className="form-content">

      <p className="tool-subtitle">This is a free tool from Ash Mann, find more at <a href="https://ashmann.co/tools" target="_blank" rel="noopener noreferrer">ashmann.co/tools <svg
      className="tool-subtitle-icon"
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12"></line>
      <polyline points="12 5 19 12 12 19"></polyline>
    </svg></a></p>
      <section style={{ textAlign: 'center', marginBottom: '2rem' }}>
       <span style={{
          display: 'inline-block',
          backgroundColor: '#dcfce7',  // light green background
          color: '#15803d',            // dark green text
          fontWeight: '600',
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.875rem',
          marginBottom: '1rem'
        }}>
          🎯 Assessment Complete
        </span>

        <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#0f172a' }}>
          Your <span style={{ color: '#DB2777' }}>Decision Fit</span> Results
        </h1>
        <p style={{ color: '#4b5563', fontSize: '1.1rem', maxWidth: '600px', margin: '1rem auto' }}>
          Based on your responses, here’s your personalised assessment and recommendations for your digital initiative.
        </p>

        <div className="mt-6 space-x-4">
        <button className="btn btn-primary"
          onClick={handleDownloadPDF}
        >
          Download your results as a PDF
        </button>

      </div>

      </section>
      

      <div className="dashboard-box" style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Overall Readiness Score</h3>
        {overallScoreOutOf100 !== null && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#DB2777' }}>
                {overallScoreOutOf100}<span style={{ fontSize: '1.25rem', color: '#64748b' }}> / 100</span>
              </div>

              <div style={{ width: '100%', maxWidth: '320px', marginTop: '0.5rem', height: '12px', backgroundColor: '#e5e7eb', borderRadius: '999px', overflow: 'hidden' }}>
                <div style={{
                  width: `${overallScoreOutOf100}%`,
                  height: '100%',
                  backgroundColor: '#DB2777',
                  transition: 'width 0.6s ease'
                }} />
              </div>

              {sectorReadinessAverage && (
                <p>
                  The average readiness score from all organisations who’ve used this tool is <strong>{sectorReadinessAverage ? Math.round(sectorReadinessAverage * 10) : '–'}</strong> out of 100.
                </p>

              )}
          </div>
        

        )}
        <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Readiness Breakdown</h3>
        <div className="chart-container">
          <RadarChartComponent
            scores={scores}
            sectorAverages={sectorAverages}
            showSectorAverage={showSectorAverage}
          />
          {sectorAverages && (
            <div className="sector-toggle-wrapper">
              <p className="sr-only" aria-live="polite">
                {showSectorAverage
                  ? 'Sector averages are now shown on the radar chart.'
                  : 'Sector averages are hidden from the radar chart.'}
              </p>
              <label className="flex items-center gap-2 text-sm">
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    id="sectorAverageToggle"
                    checked={showSectorAverage}
                    onChange={() => setShowSectorAverage(!showSectorAverage)}
                  />
                  <span className="toggle-slider"></span>
                </div>
                Show sector average on radar chart
              </label>
              <p className="sector-toggle-description">
                The sector average shows the typical readiness scores from other cultural organisations who’ve used this tool. It’s a helpful benchmark to see how your organisation compares across key areas. Use the sector average to spot opportunities: if you're below it, seek advice; if you're above, consider how you can share your experiences to help others.
              </p>
            </div>
          )}
        </div>
      </div>

      

      <div className="consultation-call" style={{ margin: '20px 0', padding: '12px', backgroundColor: '#f0f4f8', borderRadius: '6px', textAlign: 'center' }}>
        <a
          href="https://calendly.com/ash-g-d-mann/30min?month=2025-06" // your Google Calendar booking link
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontWeight: '600', color: '#DB2777', textDecoration: 'underline' }}
        >
          Book a free 30-minute call with me to discuss quick ways to improve your score.
        </a><br />
        <p>I have a limited number of these consultation slots each month. If there are no free slots this month, you can look at next month for a slot to book.</p>
      </div>
    {/* Email gate to unlock full results */}
    {!isUnlocked ? (
      <>
      <div
        className="email-gate"
        style={{
          margin: '1.25rem 0 2rem',
          padding: '1.25rem',
          borderRadius: '0.75rem',
          border: '1px solid #e5e7eb',
          background: '#fff',
          maxWidth: 600,
          marginInline: 'auto',
          textAlign: 'center'
        }}
      >
        <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.5rem' }}>
          Unlock your full results
        </h3>
        <p style={{ color: '#4b5563', marginBottom: '1rem' }}>
          Enter your email to view detailed guidance, strengths & focus areas, and next steps.
          You’ll also be subscribed to my occasional Ghost newsletter (you can unsubscribe any time).
        </p>
      
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
          <input
            type="email"
            placeholder="you@organisation.org"
            value={gateEmail}
            onChange={(e) => {
              setGateEmail(e.target.value)
              setGateError('')
            }}
            aria-label="Email address"
            style={{
              flex: '1 1 auto',
              minWidth: 240,
              padding: '0.6rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.5rem'
            }}
          />
          <button
            className="btn btn-primary"
            onClick={handleUnlock}
            disabled={gateLoading}
          >
            {gateLoading ? 'Unlocking…' : 'Unlock full results →'}
          </button>
        </div>

        {gateError && (
          <p role="alert" style={{ color: '#b91c1c', marginTop: '0.5rem' }}>{gateError}</p>
        )}

        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '0.5rem' }}>
          No spam. Just useful, occasional updates.
        </p>
      </div>
      </>
     ) : (
          <>
    
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '2rem' }}>
        <div style={{
          flex: '1 1 48%',
          backgroundColor: '#f0fdf4',
          borderRadius: '1rem',
          padding: '1.5rem',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
        }}>
      {/* 💪 Sub-question Strengths */}
      <div className="dashboard-box" style={{ flex: '1 1 320px', backgroundColor: '#ecfdf5' }}>
        <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem' }}><TrendingUp size={20} strokeWidth={2.25} style={{ color: '#047857' }} /> Your Strengths</h3>
        {Object.entries(subScores)
          .filter(([key, score]) => score >= 4)
          .map(([key]) => {
            const Icon = SUB_QUESTION_ICONS[key]
            return (
              <div key={key} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                backgroundColor: '#dcfce7',
                border: '1px solid #bbf7d0',
                borderRadius: '0.75rem',
                padding: '0.75rem 1rem',
                marginBottom: '0.75rem'
              }}>
                {Icon && <Icon size={25} strokeWidth={2} style={{ color: '#047857' }} />}
                <div>
                  <strong>{SUB_QUESTION_LABELS[key]}</strong>
                  <div>{DIMENSION_ADVICE[key]?.summary_strength}</div>
                </div>
              </div>
            )
          })}


</div>
</div>
<div style={{
    flex: '1 1 48%',
    backgroundColor: '#fffbea',
    borderRadius: '1rem',
    padding: '1.5rem',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
  }}>
{/* ⚠️ Sub-question Weaknesses */}
<div className="dashboard-box" style={{ flex: '1 1 320px', backgroundColor: '#fefbdb' }}>
  <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>⚠️ Focus Areas</h3>
  {Object.entries(subScores)
    .filter(([key, score]) => score <= 2)
    .map(([key]) => {
      const Icon = SUB_QUESTION_ICONS[key]
      return (
        <div key={key} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            backgroundColor: '#fef9c3',
            border: '1px solid #fde68a',
            borderRadius: '0.75rem',
            padding: '0.75rem 1rem',
            marginBottom: '0.75rem'
        }}>
          {Icon && <Icon size={25} strokeWidth={2} style={{ color: '#92400e' }} />}
          <div>
            <strong>{SUB_QUESTION_LABELS[key]}</strong>
            <div>{DIMENSION_ADVICE[key]?.summary_weakness}</div>
          </div>
        </div>
      )
    })}

</div>
</div>
</div>

      <section className="dashboard-section" aria-labelledby="dashboard-heading">
        <h2 className="text-xl font-semibold mb-4">Your Decision Fit dashboard</h2>
        <p className="advice-intro">
          The guidance below draws on insights from the <em>Beyond the Promise</em> research into digital failure in the cultural sector, as well as my experience supporting digital projects over the last 20 years. It’s not a prescription, just a practical starting point to help you reflect and, I hope, move forward with more confidence.
        </p>
        <div>
          {Object.entries(scores).map(([key, value]) => {
            if (!DIMENSION_ADVICE[key] || typeof value !== 'number') return null

            const subQuestions = DIMENSION_IDS[key] || []
            const subScoresForDimension = subQuestions
              .map((id) => subScores[id])
              .filter((s) => typeof s === 'number')
            const hasSubQuestions = subScoresForDimension.length > 1

            const status = getRAGStatus(value)
            const showTensionWarning =
              subScoresForDimension.length >= 2 && hasMixedSignals(subScores, subQuestions)

            return (
              <article
                key={key}
                className="dashboard-box"
                tabIndex="0"
                aria-labelledby={`heading-${key}`}
              >
                {/* Hidden heading for screen readers */}
                <h3 id={`heading-${key}`} className="sr-only">
                  {DIMENSION_ADVICE[key].title}
                </h3>

                <div className="flex items-start space-x-4">
                  {/* Title visible for all users */}
                  <div className="w-40 font-semibold advice-title">
                    {DIMENSION_ADVICE[key].title}
                  </div>

                  {/* RAG status indicator with aria-label */}
                  <div
                    className="rag-circle-wrapper"
                    role="img"
                    aria-label={`${DIMENSION_ADVICE[key].title}: ${status} readiness`}
                  ><strong>Score for this section&nbsp;&nbsp;</strong>
                    <span className={`rag-circle ${status === 'red' ? 'rag-red' : 'rag-empty'}`}></span>
                    <span className={`rag-circle ${status === 'amber' ? 'rag-amber' : 'rag-empty'}`}></span>
                    <span className={`rag-circle ${status === 'green' ? 'rag-green' : 'rag-empty'}`}></span>
                  </div>

                  {/* Mixed signals warning */}
                  {showTensionWarning && (
                    <p className="mixed-signals-flag">
                      ⚠️ <strong>Mixed signals:</strong> This area includes both strong and weak indicators. That isn’t always a problem — it might reflect a balance (e.g. strong external support but limited internal capacity). But it could also suggest hidden tensions, gaps, or dependencies. Take time to explore how these signals relate, and whether they might affect your ability to move forward confidently.
                    </p>
                  )}

                  {/* Guidance block */}
                  <div className="text-sm text-gray-700 flex-1 space-y-3">
                    {hasSubQuestions ? (
                      <>
                        {subQuestions.map((subId) => {
                          const subScore = subScores[subId]
                          if (typeof subScore !== 'number') return null
                          const subStatus = getRAGStatus(subScore)
                          const guidance =
                            subScore >= 4
                              ? DIMENSION_ADVICE[subId]?.strength
                              : subScore === 3
                              ? DIMENSION_ADVICE[subId]?.neutral
                              : DIMENSION_ADVICE[subId]?.weakness

                          return (
                            <div
                              key={subId}
                              role="group"
                              aria-labelledby={`label-${subId}`}
                              className="flex flex-col space-y-1"
                            >
                              <div className="flex items-center text-sm font-medium gap-2">
                                <span className="w-5 h-5 flex items-center justify-center">
                                  <span
                                    className={`rag-circle-sm ${
                                      subStatus === 'red'
                                        ? 'rag-red'
                                        : subStatus === 'amber'
                                        ? 'rag-amber'
                                        : 'rag-green'
                                    }`}
                                  ></span>
                                </span>
                                

                                <span id={`label-${subId}`} className="subquestion-label">
                                  {SUB_QUESTION_LABELS[subId]}
                                </span>
                              </div>

                              <div className="subquestion-guidance">{guidance}</div>
                            </div>
                          )
                        })}
                      </>
                    ) : (
                      <p>
                        {value >= 4
                          ? DIMENSION_ADVICE[key].strength
                          : value === 3
                          ? DIMENSION_ADVICE[key].neutral
                          : DIMENSION_ADVICE[key].weakness}
                      </p>
                    )}
                  </div>
                </div>
              </article>
            )
          })}

        </div>
      </section>
      
      <section className="next-steps-grid" style={{ margin: '2rem 0' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', textAlign: 'center', marginBottom: '1.5rem' }}>
          What’s Next?
        </h2>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1.5rem',
          justifyContent: 'center'
        }}>
          {/* Schedule a Follow-up */}
          <div style={{
            flex: '1 1 300px',
            backgroundColor: '#eef4ff',
            border: '1px solid #c7d2fe',
            borderRadius: '0.75rem',
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" stroke="#3b82f6" fill="none" viewBox="0 0 24 24"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1e40af' }}>Schedule a Follow-up</h3>
            </div>
            <p style={{ color: '#1e40af', fontSize: '0.95rem' }}>Book a session with me to discuss your results in detail.</p>
            <a href="https://calendly.com/ash-g-d-mann/30min" target="_blank" rel="noopener noreferrer" style={{
              fontWeight: '600',
              color: '#1e3a8a',
              marginTop: '0.5rem',
              display: 'inline-block',
              textDecoration: 'underline'
            }}>
              Schedule Now
            </a>
          </div>

          {/* Join Our Community */}
          <div style={{
            flex: '1 1 300px',
            backgroundColor: '#f5f3ff',
            border: '1px solid #ddd6fe',
            borderRadius: '0.75rem',
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" stroke="#9333ea" fill="none" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#6b21a8' }}>Join Our Community</h3>
            </div>
            <p style={{ color: '#6b21a8', fontSize: '0.95rem' }}>Connect with others doing digital work in the sector.</p>
            <a href="https://digitalworks.circle.so" target="_blank" rel="noopener noreferrer" style={{
              fontWeight: '600',
              color: '#581c87',
              marginTop: '0.5rem',
              display: 'inline-block',
              textDecoration: 'underline'
            }}>
              Join Community
            </a>
          </div>

          {/* Full-width: Download Report */}
          <div style={{
            flex: '1 1 100%',
            backgroundColor: '#ecfdf5',
            border: '1px solid #bbf7d0',
            borderRadius: '0.75rem',
            padding: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" stroke="#047857" fill="none" viewBox="0 0 24 24">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: '#065f46' }}>
                Download the <em>Beyond the Promise</em> research report into digital failure in the cultural sector.
              </p>
              <a
                href="https://ashmann.co/beyond-the-promise"
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginTop: '0.35rem', display: 'inline-block', color: '#047857', fontWeight: '600', textDecoration: 'underline' }}
              >
                Download the report
              </a>
            </div>
          </div>
        </div>
      </section>




{/*
      <section className="mt-6">
        <h2 className="text-xl font-semibold mb-2">Next Steps & Suggestions</h2>
        <p className="text-gray-700 mb-4">
          Based on your scores, it’s worth considering the following advice:
        </p>

        {strengths.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">✅ Strengths</h3>
            {strengths.map((key) => (
              <div key={key} className="mb-4">
                <h4 className="font-semibold">{DIMENSION_ADVICE[key].title}</h4>
                <p className="text-sm text-gray-700">{DIMENSION_ADVICE[key].strength}</p>
              </div>
            ))}
          </div>
        )}

        {weaknesses.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">⚠️ Weaknesses</h3>
            {weaknesses.map((key) => (
              <div key={key} className="mb-4">
                <h4 className="font-semibold">{DIMENSION_ADVICE[key].title}</h4>
                <p className="text-sm text-gray-700">{DIMENSION_ADVICE[key].weakness}</p>
              </div>
            ))}
          </div>
        )}
      </section>
*/}

      </>
     )}
      </div>
    </div>
  )
}

export default ResultsPage